import { useEffect, useState } from "react";

import { setCookie } from "@jti/fe-common";

import { DeviceContentGatePopup } from "./components/DeviceContentGatePopup/DeviceContentGatePopup";
import { getProductsOwnedCookie, productsOwnedCookieName } from "./utils/getProductOwnedCookie";
import { isProductContentRestricted } from "./utils/isProductContentRestricted";

import type { ProductFamilyOptionsType } from "./productFamily";

export type DeviceContentGateProps = {
  limitationText: string;
  confirmText: string;
  declineText: string;
  productFamily: ProductFamilyOptionsType;
  isAuthorMode?: boolean;
  shouldPageReload?: boolean;
  backButtonHref?: string;
};

export const DeviceContentGate = (deviceContentGateProps: DeviceContentGateProps) => {
  const {
    productFamily,
    isAuthorMode = false,
    shouldPageReload = false,
    backButtonHref,
    ...contentGateContent
  } = deviceContentGateProps;
  const shouldOpenPopup = isAuthorMode || isProductContentRestricted(productFamily);

  const [isPopupOpen, setIsPopupOpen] = useState(shouldOpenPopup);

  useEffect(() => {
    setIsPopupOpen(shouldOpenPopup);
  }, [shouldOpenPopup]);

  const handleConfirm = () => {
    if (isAuthorMode) {
      return;
    }

    const productsOwnedSet = new Set(getProductsOwnedCookie());

    productsOwnedSet.add(productFamily);
    setCookie(productsOwnedCookieName, JSON.stringify([...productsOwnedSet]), "/");

    // In hard gate mode the document is the standalone gate page, so the real content only appears after a reload.
    if (shouldPageReload) {
      window.location.reload();

      return;
    }

    setIsPopupOpen(false);
  };

  const handleDecline = () => {
    if (isAuthorMode) {
      return;
    }

    if (backButtonHref) {
      window.location.assign(backButtonHref);

      return;
    }

    const hasPreviousEntry = window.history.length > 1;

    if (hasPreviousEntry && document.referrer) {
      const previousUrl = new URL(document.referrer);

      if (previousUrl.origin === window.location.origin) {
        window.history.back();

        return;
      }
    }

    window.location.assign("/");
  };

  return (
    <DeviceContentGatePopup
      isOpen={isPopupOpen}
      onConfirm={handleConfirm}
      onDecline={handleDecline}
      isAuthorMode={isAuthorMode}
      {...contentGateContent}
    />
  );
};
